import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProfile,
  updateProfileFields,
  upsertExperience,
  deleteExperience,
  upsertEducation,
  deleteEducation,
  upsertCertification,
  deleteCertification,
  upsertHighlight,
  deleteHighlight,
  upsertProject,
  deleteProject,
  upsertServiceOffer,
  deleteServiceOffer,
  saveSkillGroups,
  saveLanguages,
  saveJobPreferences,
  savePrivateInfo,
  saveCvConfig,
  saveSectionsConfig,
  saveProfilePrivacy,
} from '../lib/profile-api';
import { refreshAuthenticatedUser } from '../lib/app-auth';
import { CVDataContext, type CVSkillGroup, type CVConfig } from '../lib/cv-mock-data';
import { MASTER_CV_ID } from '../lib/cv-main-default';
import type { CvSectionsConfig } from '../lib/cv-section-config';
import type { ProfileResponse, ProfileTarget } from '../lib/profile-api';
import { captureSentryException } from '../lib/sentry';
import { invalidateCvDocumentQueries } from '../lib/cv-query-invalidation';
import { cvRevisionTarget, cvWriteArgs } from '../lib/cv-write-coordinator';
import { PROFILE_WRITE_POLICY } from '../lib/cv-write-policy';
import { useCvRevisionTracking, useCvWriteRunner } from './useCvWriteRunner';

/**
 * Collapse the two names for one record.
 *
 * The main WebCV is addressed as "master" when it is reached as a CV surface
 * (`/cv?cvTarget=master`) and as no-target when it is reached as the user's own
 * profile (`/cv`, the jobs screens, profile settings). Both read the same
 * `Profile` row — `GET /api/cv/master/profile` and `GET /api/profile` both
 * return `ProfileService.getFullProfile(userId)` — so caching them apart meant
 * an edit made on one surface left the other showing pre-edit data until its
 * entry went stale.
 *
 * READS only. Writes keep the caller's target so a master edit still goes
 * through `PATCH /api/cv/master/profile` and records its CV activity-log entry.
 * Delegated (`grant:` / `access:`) and tailored-CV targets are untouched: those
 * really are different records.
 */
function normalizeProfileReadTarget(target?: ProfileTarget): ProfileTarget {
  const normalized = typeof target === 'string' ? target.trim() : '';
  return !normalized || normalized === MASTER_CV_ID ? null : normalized;
}

const profileKey = (target?: ProfileTarget) =>
  ['profile', normalizeProfileReadTarget(target) || 'self'] as const;

function invalidateJobCatalogQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['job-catalog'] });
}

function useResolvedProfileTarget(target?: ProfileTarget) {
  const cvContext = useContext(CVDataContext);
  return target ?? cvContext?.target ?? null;
}

// ─── Fetch Profile ─────────────────────────────────────────────

export function useProfile(enabled = true, target?: ProfileTarget) {
  // Normalized for the fetch as well as the key: one cache entry served by two
  // different fetchers would let mount order decide which endpoint runs.
  const readTarget = normalizeProfileReadTarget(useResolvedProfileTarget(target));
  // A CV target's read reports its revision: the base its saves state (B02).
  useCvRevisionTracking();
  return useQuery<ProfileResponse>({
    queryKey: profileKey(readTarget),
    queryFn: () => fetchProfile(readTarget),
    enabled,
    staleTime: 30_000,
  });
}

// ─── Update Profile Scalars ────────────────────────────────────

export function useUpdateProfile(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (patch: Parameters<typeof updateProfileFields>[0]) =>
      runCvWrite(PROFILE_WRITE_POLICY.profile, (write) =>
        updateProfileFields(patch, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: async () => {
      invalidateCvDocumentQueries(queryClient, resolvedTarget);
      invalidateJobCatalogQueries(queryClient);
      if (!resolvedTarget) {
        try {
          await refreshAuthenticatedUser();
        } catch {
          // Best effort auth user refresh for avatar/name changes.
        }
      }
    },
  });
}

// ─── Experience ────────────────────────────────────────────────

export function useUpsertExperience(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (input: Parameters<typeof upsertExperience>[0]) =>
      runCvWrite(PROFILE_WRITE_POLICY.item, (write) =>
        upsertExperience(input, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () => {
      invalidateCvDocumentQueries(queryClient, resolvedTarget);
      invalidateJobCatalogQueries(queryClient);
    },
  });
}

export function useDeleteExperience(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (id: string) =>
      runCvWrite(PROFILE_WRITE_POLICY.item, (write) =>
        deleteExperience(id, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () => {
      invalidateCvDocumentQueries(queryClient, resolvedTarget);
      invalidateJobCatalogQueries(queryClient);
    },
  });
}

// ─── Education ─────────────────────────────────────────────────

export function useUpsertEducation(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (input: Parameters<typeof upsertEducation>[0]) =>
      runCvWrite(PROFILE_WRITE_POLICY.item, (write) =>
        upsertEducation(input, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

export function useDeleteEducation(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (id: string) =>
      runCvWrite(PROFILE_WRITE_POLICY.item, (write) =>
        deleteEducation(id, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

// ─── Certification ─────────────────────────────────────────────

export function useUpsertCertification(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (input: Parameters<typeof upsertCertification>[0]) =>
      runCvWrite(PROFILE_WRITE_POLICY.item, (write) =>
        upsertCertification(input, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

export function useDeleteCertification(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (id: string) =>
      runCvWrite(PROFILE_WRITE_POLICY.item, (write) =>
        deleteCertification(id, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

// ─── Career Highlight ──────────────────────────────────────────

export function useUpsertHighlight(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (input: Parameters<typeof upsertHighlight>[0]) =>
      runCvWrite(PROFILE_WRITE_POLICY.item, (write) =>
        upsertHighlight(input, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

export function useDeleteHighlight(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (id: string) =>
      runCvWrite(PROFILE_WRITE_POLICY.item, (write) =>
        deleteHighlight(id, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

// ─── Projects ────────────────────────────────────────────────────

export function useUpsertProject(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (input: Parameters<typeof upsertProject>[0]) =>
      runCvWrite(PROFILE_WRITE_POLICY.project, (write) =>
        upsertProject(input, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () => invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

export function useDeleteProject(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (input: Parameters<typeof deleteProject>[0]) =>
      runCvWrite(PROFILE_WRITE_POLICY.project, (write) =>
        deleteProject(input, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () => invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

// ─── Service Offers ──────────────────────────────────────────────

export function useUpsertServiceOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: upsertServiceOffer,
    onSuccess: () => invalidateCvDocumentQueries(queryClient),
  });
}

export function useDeleteServiceOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteServiceOffer,
    onSuccess: () => invalidateCvDocumentQueries(queryClient),
  });
}

// ─── Skills ────────────────────────────────────────────────────

export function useSaveSkillGroups(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (groups: CVSkillGroup[]) =>
      runCvWrite(PROFILE_WRITE_POLICY.list, (write) =>
        saveSkillGroups(groups, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () => {
      invalidateCvDocumentQueries(queryClient, resolvedTarget);
      invalidateJobCatalogQueries(queryClient);
    },
  });
}

// ─── Languages ─────────────────────────────────────────────────

export function useSaveLanguages(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (languages: Parameters<typeof saveLanguages>[0]) =>
      runCvWrite(PROFILE_WRITE_POLICY.list, (write) =>
        saveLanguages(languages, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

// ─── Job Preferences ───────────────────────────────────────────

export function useSaveJobPreferences(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  return useMutation({
    mutationFn: (prefs: Parameters<typeof saveJobPreferences>[0]) =>
      saveJobPreferences(prefs, resolvedTarget),
    onSuccess: () => {
      invalidateCvDocumentQueries(queryClient, resolvedTarget);
      invalidateJobCatalogQueries(queryClient);
    },
    onError: (err, variables) => {
      // Mutation-level capture so the bare catch in EditJobPreferencesForm
      // and any other caller don't have to remember to log themselves.
      captureSentryException(err, {
        scope: 'use_save_job_preferences',
        searchAreaCount: variables?.searchAreas?.length ?? 0,
      });
    },
  });
}

// ─── Private Info (demographic / eligibility) ──────────────────

export function useSavePrivateInfo(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  return useMutation({
    mutationFn: (info: Parameters<typeof savePrivateInfo>[0]) =>
      savePrivateInfo(info, resolvedTarget),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

// ─── CV Config ─────────────────────────────────────────────────

export function useSaveCvConfig(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    // CV config is a full-document PUT. Serializing saves for the same target
    // prevents an older photo-only snapshot from landing after a newer
    // one-page change (or vice versa). On a CV the write coordinator does it
    // (B02: one save at a time, chained revisions, the newest pending config
    // replaces an unsent older one); the main profile keeps the mutation scope.
    scope: cvRevisionTarget(resolvedTarget)
      ? undefined
      : { id: `cv-config:${resolvedTarget ?? 'master'}` },
    mutationFn: (config: CVConfig) =>
      runCvWrite(PROFILE_WRITE_POLICY.cvConfig, (write) =>
        saveCvConfig(config, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

export function useSaveSectionsConfig(target?: ProfileTarget) {
  const queryClient = useQueryClient();
  const resolvedTarget = useResolvedProfileTarget(target);
  const runCvWrite = useCvWriteRunner(resolvedTarget);
  return useMutation({
    mutationFn: (config: CvSectionsConfig) =>
      runCvWrite(PROFILE_WRITE_POLICY.sectionsConfig, (write) =>
        saveSectionsConfig(config, resolvedTarget, ...cvWriteArgs(write)),
      ),
    onSuccess: () =>
      invalidateCvDocumentQueries(queryClient, resolvedTarget),
  });
}

// ─── Privacy ───────────────────────────────────────────────────

export function useSaveProfilePrivacy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveProfilePrivacy,
    onSuccess: () => {
      invalidateCvDocumentQueries(queryClient);
    },
  });
}
